import React, { useState, useEffect, useCallback, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Sparkles, Wand2, ArrowRight, Target } from "lucide-react";
import { postAllocator, getProfile, postGoalsPlan, updateGoal } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Overline, Badge, Loader } from "../components/Primitives";
import { AC_COLORS, POS, NEG, fmtPct } from "../lib/format";

const RISK_LABELS = { conservative: "Conservative", balanced: "Balanced", aggressive: "Aggressive" };
const defaultRisk = (year) => { const y = (year || 2030) - 2026; return y < 3 ? "conservative" : y <= 7 ? "balanced" : "aggressive"; };

export default function Rebalancer() {
  const { money, currency, usdInr } = useApp();
  const [deposit, setDeposit] = useState(100000);
  const [stepUp, setStepUp] = useState(12);
  const [years, setYears] = useState(10);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try { setRes(await postAllocator({ deposit, step_up: stepUp, years })); }
    finally { setLoading(false); }
  }, [deposit, stepUp, years]);

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const conv = (v) => (currency === "USD" ? v / usdInr : v);

  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Controls */}
        <Card className="p-5" testid="allocator-controls">
          <div className="flex items-center gap-2 mb-4"><Wand2 className="w-4 h-4 text-[#00e5ff]" /><Overline>Capital Deposit Planner</Overline></div>

          <label className="overline">Monthly Deposit (SIP)</label>
          <div className="flex items-center gap-2 mt-1 mb-2 rounded-lg border border-[#2d3c59] px-3 py-2">
            <span className="font-mono text-[#64748b]">{currency === "USD" ? "$" : "₹"}</span>
            <input data-testid="rebalance-deposit-input" type="number" value={Math.round(conv(deposit))}
              onChange={(e) => setDeposit(currency === "USD" ? +e.target.value * usdInr : +e.target.value)}
              className="bg-transparent outline-none font-mono text-lg w-full text-[#f8fafc]" />
          </div>
          <input type="range" min="6000" max="1000000" step="1000" value={deposit} onChange={(e) => setDeposit(+e.target.value)} className="w-full" data-testid="deposit-slider" />
          <div className="flex justify-between text-[10px] font-mono text-[#64748b] mt-1"><span>{money(6000, { compact: true })}</span><span>{money(1000000, { compact: true })}</span></div>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <div>
              <label className="overline">Annual Step-Up</label>
              <div className="flex items-center gap-1 mt-1 rounded-lg border border-[#2d3c59] px-3 py-2">
                <input data-testid="stepup-input" type="number" value={stepUp} onChange={(e) => setStepUp(+e.target.value)} className="bg-transparent outline-none font-mono w-full text-[#f8fafc]" />
                <span className="font-mono text-[#64748b]">%</span>
              </div>
            </div>
            <div>
              <label className="overline">Horizon</label>
              <div className="flex items-center gap-1 mt-1 rounded-lg border border-[#2d3c59] px-3 py-2">
                <input data-testid="years-input" type="number" value={years} onChange={(e) => setYears(Math.max(1, Math.min(30, +e.target.value)))} className="bg-transparent outline-none font-mono w-full text-[#f8fafc]" />
                <span className="font-mono text-[#64748b]">yrs</span>
              </div>
            </div>
          </div>

          <button data-testid="execute-rebalance-btn" onClick={run}
            className="mt-5 w-full flex items-center justify-center gap-2 rounded-lg bg-[#00e5ff] text-[#06121a] font-medium py-2.5 hover:bg-[#33eaff] transition-colors">
            <Sparkles className="w-4 h-4" /> Generate Distribution Plan
          </button>
        </Card>

        {/* Distribution plan */}
        <Card className="lg:col-span-2 p-5" testid="distribution-plan">
          <div className="flex items-center justify-between mb-4">
            <Overline>One-Click Distribution · {money(deposit)}/mo</Overline>
            <Badge color="#00e676">Buy-only · No sell friction</Badge>
          </div>
          {loading || !res ? <Loader label="Optimising allocation…" /> : (
            <div className="space-y-3">
              {res.plan.map((p) => (
                <div key={p.asset_class} className="flex items-center gap-3 rounded-lg border border-[#1e293b] p-3 hover:border-[#2d3c59] transition-colors" data-testid={`plan-row-${p.asset_class}`}>
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: AC_COLORS[p.asset_class] }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#f8fafc]">{p.label}</div>
                    <div className="text-xs text-[#64748b] truncate">{p.reason}{p.instrument ? ` · via ${p.instrument}` : ""}</div>
                  </div>
                  {p.units != null && <div className="font-mono text-xs text-[#94a3b8] hidden sm:block">{p.units} units</div>}
                  <ArrowRight className="w-4 h-4 text-[#64748b] hidden sm:block" />
                  <div className="font-mono text-base font-semibold text-[#00e5ff] text-right w-24">{money(p.amount_inr, { compact: true })}</div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 font-mono text-sm">
                <span className="text-[#64748b]">Total deployed this month</span>
                <span className="text-[#f8fafc] font-semibold">{money(res.plan.reduce((s, p) => s + p.amount_inr, 0))}</span>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Drift visualizer */}
      {res && (
        <Card className="p-5" testid="drift-visualizer">
          <Overline className="mb-4">Allocation Drift · Current vs Strategic Target</Overline>
          <div className="space-y-3.5">
            {res.allocation.filter((a) => a.value_inr > 0 || a.target > 0).map((a) => (
              <div key={a.asset_class} data-testid={`drift-row-${a.asset_class}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#94a3b8]">{a.label}</span>
                  <span className="font-mono"><span className="text-[#f8fafc]">{a.weight.toFixed(1)}%</span> <span className="text-[#64748b]">/ {a.target}%</span> <span style={{ color: Math.abs(a.drift) >= 5 ? "#ffb300" : "#64748b" }}>({fmtPct(a.drift, 1)})</span></span>
                </div>
                <div className="relative h-2.5 rounded-full bg-[#1e293b]">
                  <div className="absolute top-0 bottom-0 w-0.5 bg-[#f8fafc] z-10" style={{ left: `${a.target}%` }} title="target" />
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, a.weight)}%`, background: AC_COLORS[a.asset_class] }} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-4 text-[11px] text-[#64748b] font-mono">
            <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded bg-[#00e5ff]" /> Current weight</span>
            <span className="flex items-center gap-1.5"><span className="w-0.5 h-3 bg-[#f8fafc]" /> Strategic target</span>
          </div>
        </Card>
      )}

      {/* Step-up forecast */}
      {res && (
        <Card className="p-5" testid="sip-forecast">
          <div className="flex items-center justify-between mb-2">
            <Overline>Step-Up SIP Wealth Projection · {years}Y @ {stepUp}% annual step-up</Overline>
            <span className="font-mono text-sm">Projected: <span className="text-[#00e676]">{money(res.forecast[res.forecast.length - 1].corpus, { compact: true })}</span></span>
          </div>
          <div className="h-[280px] -ml-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={res.forecast}>
                <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={(y) => `Y${y}`} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10, fontFamily: "IBM Plex Mono" }} width={60} tickFormatter={(v) => money(v, { compact: true })} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0f1420", border: "1px solid #2d3c59", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  labelFormatter={(y) => `Year ${y}`} formatter={(v, n) => [money(v, { compact: true }), n === "invested" ? "Invested" : "Gains"]} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono", color: "#94a3b8" }} />
                <Bar dataKey="invested" stackId="a" fill="#334155" name="invested" radius={[0, 0, 0, 0]} />
                <Bar dataKey="gains" stackId="a" fill="#00e676" name="gains" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <GoalRebalancer money={money} />
    </div>
  );
}

function GoalRebalancer({ money }) {
  const [goals, setGoals] = useState([]);
  const [risks, setRisks] = useState({});
  const [sip, setSip] = useState(50000);
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getProfile().then((d) => {
      const gs = d.goals || [];
      setGoals(gs);
      const init = {};
      gs.forEach((g) => { init[g.id] = g.risk_profile || defaultRisk(g.year); });
      setRisks(init);
    });
  }, []);

  // Auto-split: proportional to each goal's unfunded gap (target - current).
  // Goals arrive sorted by priority; rounding remainder goes to highest-priority goals.
  const shares = useMemo(() => {
    const out = {};
    if (!goals.length) return out;
    const gaps = goals.map((g) => Math.max(0, (g.target || 0) - (g.current || 0)));
    const totalGap = gaps.reduce((s, x) => s + x, 0);
    let acc = 0;
    goals.forEach((g, i) => {
      const amt = Math.floor(totalGap > 0 ? (sip * gaps[i]) / totalGap : sip / goals.length);
      out[g.id] = amt;
      acc += amt;
    });
    let rem = Math.round(sip) - acc;
    for (let i = 0; rem > 0 && i < goals.length; i++) { out[goals[i].id] += 1; rem -= 1; }
    return out;
  }, [goals, sip]);

  const setRisk = async (g, rk) => {
    setRisks((s) => ({ ...s, [g.id]: rk }));
    try { const r = await updateGoal(g.id, { risk_profile: rk }); setGoals(r.goals); } catch { /* local state already updated */ }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const body = { plans: goals.map((g) => ({ goal_id: g.id, deposit: shares[g.id] || 0, risk_profile: risks[g.id] })) };
      const d = await postGoalsPlan(body);
      setPlans(d.plans);
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5" testid="goal-rebalancer">
      <div className="flex items-center gap-2 mb-1"><Target className="w-4 h-4 text-[#a855f7]" /><Overline>Per-Goal Rebalancer · one SIP, auto-split by need & priority</Overline></div>
      <p className="text-xs text-[#64748b] mb-4">Enter a single monthly SIP — it is split across goals proportional to each goal's remaining need (target − current), with priority breaking ties. Each goal's saved risk mix decides the asset routing. Drag goals in Retirement &amp; Goals to change priority.</p>

      {goals.length === 0 ? <div className="text-sm text-[#64748b] text-center py-4">No goals yet. Add goals in Retirement & Goals.</div> : (
        <>
          <label className="overline">Total Monthly SIP (auto-split)</label>
          <div className="flex items-center gap-2 mt-1 mb-2 rounded-lg border border-[#2d3c59] px-3 py-2 max-w-sm">
            <span className="font-mono text-[#64748b]">₹</span>
            <input data-testid="goal-sip-input" type="number" value={sip}
              onChange={(e) => setSip(Math.max(0, +e.target.value))}
              className="bg-transparent outline-none font-mono text-lg w-full text-[#f8fafc]" />
          </div>
          <input type="range" min="6000" max="1000000" step="1000" value={Math.min(1000000, sip)} onChange={(e) => setSip(+e.target.value)} className="w-full max-w-sm" data-testid="goal-sip-slider" />

          <div className="space-y-3 mt-4">
            {goals.map((g, idx) => {
              const gap = Math.max(0, (g.target || 0) - (g.current || 0));
              return (
                <div key={g.id} className="rounded-lg border border-[#1e293b] p-3 flex flex-wrap items-center gap-3" data-testid={`goal-input-${g.id}`}>
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-sm font-medium text-[#f8fafc]"><span className="font-mono text-[10px] text-[#a855f7] mr-1.5">P{idx + 1}</span>{g.name}</div>
                    <div className="font-mono text-[11px] text-[#64748b]">{g.year} · remaining need {money(gap, { compact: true })}</div>
                  </div>
                  <div className="text-right">
                    <div className="overline">Auto share</div>
                    <div className="font-mono text-sm font-semibold text-[#00e5ff]" data-testid={`goal-share-${g.id}`}>{money(shares[g.id] || 0, { compact: true })}</div>
                  </div>
                  <div className="flex rounded-lg border border-[#2d3c59] overflow-hidden">
                    {Object.keys(RISK_LABELS).map((rk) => (
                      <button key={rk} data-testid={`goal-risk-${g.id}-${rk}`} onClick={() => setRisk(g, rk)}
                        className={`px-2.5 py-1.5 text-[11px] transition-colors ${risks[g.id] === rk ? "bg-[#1a2338] text-[#a855f7]" : "text-[#64748b] hover:text-[#94a3b8]"}`}>{RISK_LABELS[rk]}</button>
                    ))}
                  </div>
                </div>
              );
            })}
            <button data-testid="goal-plan-btn" onClick={generate} disabled={busy}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#a855f7] text-white font-medium py-2.5 hover:bg-[#9333ea] disabled:opacity-50 transition-colors">
              <Wand2 className="w-4 h-4" /> {busy ? "Computing…" : `Generate Goal-by-Goal Plan · ${money(sip, { compact: true })}`}
            </button>
          </div>
        </>
      )}

      {plans && (
        <div className="mt-5 space-y-4">
          {plans.map((p) => (
            <div key={p.goal_id} className="rounded-lg border border-[#1e293b] p-4" data-testid={`goal-plan-${p.goal_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <span className="font-semibold text-[#f8fafc]">{p.name}</span>
                <div className="flex items-center gap-2"><Badge color="#a855f7">{RISK_LABELS[p.risk_profile]}</Badge><span className="font-mono text-xs text-[#94a3b8]">Deploy {money(p.deposit, { compact: true })}</span></div>
              </div>
              {p.plan.length === 0 ? <div className="text-xs text-[#64748b]">This goal is already at target mix — deposit held as cash buffer.</div> : (
                <div className="space-y-2">
                  {p.plan.map((row) => (
                    <div key={row.asset_class} className="flex items-center gap-3 text-sm" data-testid={`goal-plan-row-${p.goal_id}-${row.asset_class}`}>
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: AC_COLORS[row.asset_class] }} />
                      <span className="flex-1 text-[#94a3b8]">{row.label} <span className="text-[#64748b] text-xs">({row.current}%→{row.target}%)</span>{row.instrument ? <span className="text-[#64748b] text-xs"> · {row.instrument}</span> : ""}</span>
                      <span className="font-mono text-[#00e5ff] font-semibold">{money(row.amount_inr, { compact: true })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
